import React from 'react'
import type { CandleInterval } from '@nutrimatic/sdk'

import type { OnChainPairData } from '@/features/market/useOnChainPairData'
import type { AddLiquidityControllerResult } from '@/features/liquidity/useAddLiquidityController'
import type { ImportPoolControllerResult } from '@/features/liquidity/useImportPoolController'
import type { LiquidityPositionsControllerResult } from '@/features/liquidity/useLiquidityPositionsController'
import type { RemoveLiquidityControllerResult } from '@/features/liquidity/useRemoveLiquidityController'
import type { ResolveWidgetRoute } from '@/widgets/navigation'
import type { WidgetRouteIntent } from '@/widgets/navigation/widgetRouteIntent'

import { MobileRemoveLiquidityRateHero } from '../mobile/MobileRemoveLiquidityQuoteBlocks'
import { MobileImportPoolRateHero } from '../mobile/LiquidityNutrimaticMobileImportView'
import { useLiquidityAddNutrimaticFlow } from '../useLiquidityAddNutrimaticFlow'
import { useLiquidityRemoveNutrimaticFlow } from '../useLiquidityRemoveNutrimaticFlow'
import { LiquidityNutrimaticAddPerformStack } from '../tablet/LiquidityNutrimaticAddPerformStack'
import { LiquidityNutrimaticImportPerformStack } from '../tablet/LiquidityNutrimaticImportPerformStack'
import { LiquidityNutrimaticRemovePerformStack } from '../tablet/LiquidityNutrimaticRemovePerformStack'
import { LiquidityNutrimaticTabletChartPane } from '../tablet/LiquidityNutrimaticTabletChartPane'
import { LiquidityNutrimaticTabletDeviceShell } from '../tablet/LiquidityNutrimaticTabletDeviceShell'
import { LiquidityNutrimaticTabletMarketPane } from '../tablet/LiquidityNutrimaticTabletMarketPane'
import { useNutrimaticMarketOptional } from '@/features/market/NutrimaticMarketProvider'

import { LiquidityNutrimaticPositionsInstrument } from './LiquidityNutrimaticPositionsInstrument'
import { LiquidityNutrimaticReadoutPane } from './LiquidityNutrimaticReadoutPane'
import { useLiquidityPositionsHubFlow } from './useLiquidityPositionsHubFlow'

export type LiquidityNutrimaticHubMode = 'positions' | 'add' | 'remove' | 'import'

type PositionsShellProps = {
  mode: 'positions'
  controller: LiquidityPositionsControllerResult
  resolveRoute: ResolveWidgetRoute
}

type AddShellProps = {
  mode: 'add'
  controller: AddLiquidityControllerResult
  pairData: OnChainPairData
}

type RemoveShellProps = {
  mode: 'remove'
  controller: RemoveLiquidityControllerResult
  pairData: OnChainPairData
}

type ImportShellProps = {
  mode: 'import'
  controller: ImportPoolControllerResult
  pairData: OnChainPairData
  resolveRoute: (intent: WidgetRouteIntent) => string
}

export type LiquidityNutrimaticUnifiedTabletShellProps =
  | PositionsShellProps
  | AddShellProps
  | RemoveShellProps
  | ImportShellProps

function PositionsTabletShell({ controller, resolveRoute }: PositionsShellProps) {
  const { selectedPair, setSelectedPair } = useLiquidityPositionsHubFlow(controller.allV2PairsWithLiquidity)

  return (
    <LiquidityNutrimaticTabletDeviceShell
      performScreen="positions"
      readout={<LiquidityNutrimaticReadoutPane controller={controller} selectedPair={selectedPair} />}
      perform={
        <div className="nm-tablet-trade__stage-stack" data-nutrimatic-mobile-simple>
          <div className="nm-mobile-simple__stage flex min-h-0 flex-1 flex-col overflow-hidden">
            <LiquidityNutrimaticPositionsInstrument
              controller={controller}
              resolveRoute={resolveRoute}
              selectedPair={selectedPair}
              onSelectPair={setSelectedPair}
            />
          </div>
        </div>
      }
    />
  )
}

function useHubChartInterval() {
  const hub = useNutrimaticMarketOptional()
  return {
    chartTimeframe: (hub?.chartInterval ?? '1h') as CandleInterval,
    setChartTimeframe: hub?.setChartInterval ?? ((_interval: CandleInterval) => {}),
  }
}

function AddTabletShell({ controller, pairData }: AddShellProps) {
  const { screen } = useLiquidityAddNutrimaticFlow(controller)
  const { chartTimeframe, setChartTimeframe } = useHubChartInterval()

  return (
    <LiquidityNutrimaticTabletDeviceShell
      performScreen={screen}
      readout={
        <LiquidityNutrimaticTabletChartPane
          controller={controller}
          pairData={pairData}
          chartTimeframe={chartTimeframe}
          onChartTimeframeChange={setChartTimeframe}
        />
      }
      perform={<LiquidityNutrimaticAddPerformStack controller={controller} />}
    />
  )
}

function RemoveTabletShell({ controller, pairData }: RemoveShellProps) {
  const { screen } = useLiquidityRemoveNutrimaticFlow(controller)
  const { chartTimeframe, setChartTimeframe } = useHubChartInterval()

  return (
    <LiquidityNutrimaticTabletDeviceShell
      performScreen={screen}
      readout={
        <LiquidityNutrimaticTabletMarketPane
          currencyA={controller.currencyA}
          currencyB={controller.currencyB}
          pairData={pairData}
          rateHero={
            <MobileRemoveLiquidityRateHero currencyA={controller.currencyA} currencyB={controller.currencyB} />
          }
          chartTimeframe={chartTimeframe}
          onChartTimeframeChange={setChartTimeframe}
        />
      }
      perform={<LiquidityNutrimaticRemovePerformStack controller={controller} />}
    />
  )
}

function ImportTabletShell({ controller, pairData, resolveRoute }: ImportShellProps) {
  const { chartTimeframe, setChartTimeframe } = useHubChartInterval()

  return (
    <LiquidityNutrimaticTabletDeviceShell
      performScreen="form"
      readout={
        <LiquidityNutrimaticTabletMarketPane
          currencyA={controller.currency0}
          currencyB={controller.currency1}
          pairData={pairData}
          rateHero={<MobileImportPoolRateHero currency0={controller.currency0} currency1={controller.currency1} />}
          chartTimeframe={chartTimeframe}
          onChartTimeframeChange={setChartTimeframe}
        />
      }
      perform={<LiquidityNutrimaticImportPerformStack controller={controller} resolveRoute={resolveRoute} />}
    />
  )
}

/** Tableta desktop unificada del hub Nutrimatic LP (readout | perform según modo). */
export function LiquidityNutrimaticUnifiedTabletShell(props: LiquidityNutrimaticUnifiedTabletShellProps) {
  switch (props.mode) {
    case 'positions':
      return <PositionsTabletShell {...props} />
    case 'add':
      return <AddTabletShell {...props} />
    case 'remove':
      return <RemoveTabletShell {...props} />
    case 'import':
      return <ImportTabletShell {...props} />
  }
}
